import React, { useEffect, useState, useCallback, memo } from 'react';
import { Checkbox, Avatar, Button, IconButton } from '@mui/material';
import {
	TextField,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CONSTANTS from '../../utils/constants/UIConstants';
interface AlarmTableProps {
	locations: any;
	twoWayAlarm: boolean;
	onPrepareData: (data: any[]) => void;
	isOwner?: boolean;
}

interface Alarm {
	[key: string]: any;
}

interface AlarmsState {
	[key: string]: Alarm;
}

const statusColors = {
	immediate: '#FEE4E2', // Replace with actual color codes
	preventative: '#FEF0C7',
	goodControl: '#D0EFB4',
};

const StyledTableCell = styled(TableCell)({
	padding: 0, // Reset padding for the cell
});

const ColorBoxG = styled(Box)(({ theme }) => ({
	display: 'inline-block', // Make ColorBox inline
	verticalAlign: 'middle', // Align vertically if needed
	width: '40px', // Adjust the width as needed
	height: '40px', // Adjust the height to match the line height of the text
	backgroundColor: '#D0EFB4', // Your color
}));

const ColorBoxY = styled(Box)(({ theme }) => ({
	display: 'inline-block', // Make ColorBox inline
	verticalAlign: 'middle', // Align vertically if needed
	width: '40px', // Adjust the width as needed
	height: '40px', // Adjust the height to match the line height of the text
	backgroundColor: '#FEF0C7', // Your color
}));

const ColorBoxR = styled(Box)(({ theme }) => ({
	display: 'inline-block', // Make ColorBox inline
	verticalAlign: 'middle', // Align vertically if needed
	width: '40px', // Adjust the width as needed
	height: '40px', // Adjust the height to match the line height of the text
	backgroundColor: '#FEE4E2', // Your color
}));

interface AlarmRowProps {
	key: string;
	location: any; // You should replace 'any' with a more specific type if possible
	descriptor: any; // Same as above, use a more specific type if possible
	alarm: any; // Same as above
	twoWayAlarm: boolean;
	handleAlarmChange: (
		locationId: number,
		testKitId: number,
		alarmId: number,
		key: string,
		value: any
	) => void;
	statusColors: {
		[key: string]: string;
	};
	isOwner?: boolean;
}

const AlarmRow: React.FC<AlarmRowProps> = ({
	location,
	descriptor,
	alarm,
	twoWayAlarm,
	handleAlarmChange,
	statusColors,
	isOwner,
}) => {
	return (
		<div className="grid grid-cols-9 font-jost text-[14px] text-[#2C2C2C] border-b-[1px] border-[#E6E6E6]">
			<div className="col-span-2 pt-2">{location.name}</div>
			<div className="col-span-1 pt-2">{descriptor.testKitName}</div>
			<div className={` pt-2 col-span-${twoWayAlarm ? '1' : '3'}`}>
				{alarm?.outPutType}
			</div>
			{twoWayAlarm && (

					<div className="flex col-span-5 ml-[75px]">
						<ColorBoxR bgcolor={statusColors.preventative} />
						<TextField
							defaultValue={alarm?.loLoValue}
							disabled={!isOwner}
							onChange={(e) =>
								handleAlarmChange(
									location.id,
									descriptor.id,
									alarm?.outPutType,
									'loLoValue',
									e.target.value
								)
							}
							size="small"
							style={{
								maxWidth: '87px',
							}}
							InputProps={{
								style: {
									borderRadius: '0px',
								},
							}}
						/>

						<ColorBoxY bgcolor={statusColors.preventative} />
						<TextField
							defaultValue={alarm?.loValue}
							disabled={!isOwner}
							onChange={(e) =>
								handleAlarmChange(
									location.id,
									descriptor.id,
									alarm?.outPutType,
									'loValue',
									e.target.value
								)
							}
							size="small"
							style={{
								maxWidth: '87px',
							}}
							InputProps={{
								style: {
									borderRadius: '0px',
								},
							}}
						/>
						<ColorBoxG bgcolor={statusColors.preventative} />
						<TextField
							defaultValue={alarm?.hiValue}
							onChange={(e) =>
								handleAlarmChange(
									location.id,
									descriptor.id,
									alarm?.outPutType,
									'hiValue',
									e.target.value
								)
							}
							size="small"
							style={{
								maxWidth: '87px',
							}}
							InputProps={{
								style: {
									borderRadius: '0px',
								},
							}}
						/>

						<ColorBoxY bgcolor={statusColors.preventative} />
						<TextField
							defaultValue={alarm?.hiHiValue}
							disabled={!isOwner}
							onChange={(e) =>
								handleAlarmChange(
									location.id,
									descriptor.id,
									alarm?.outPutType,
									'hiHiValue',
									e.target.value
								)
							}
							size="small"
							style={{
								maxWidth: '87px',
							}}
							InputProps={{
								style: {
									borderRadius: '0px',
								},
							}}
						/>
						<ColorBoxR bgcolor={statusColors.preventative} />
					</div>

			)}

			{!twoWayAlarm && (
				<div className="col-span-3 ml-[75px]">
					<ColorBoxG bgcolor={statusColors.preventative} />
					<TextField
						defaultValue={alarm?.hiValue}
						disabled={!isOwner}
						onChange={(e) =>
							handleAlarmChange(
								location.id,
								descriptor.id,
								alarm?.outPutType,
								'hiValue',
								e.target.value
							)
						}
						size="small"
						style={{
							maxWidth: '87px',
						}}
						InputProps={{
							style: {
								borderRadius: '0px',
							},
						}}
					/>

					<ColorBoxY bgcolor={statusColors.preventative} />
					<TextField
						defaultValue={alarm?.hiHiValue}
						disabled={!isOwner}
						size="small"
						style={{
							maxWidth: '87px',
						}}
						onChange={(e) =>
							handleAlarmChange(
								location.id,
								descriptor.id,
								alarm?.outPutType,
								'hiHiValue',
								e.target.value
							)
						}
						InputProps={{
							style: {
								borderRadius: '0px',
							},
						}}
					/>
					<ColorBoxR bgcolor={statusColors.preventative} />
				</div>
			)}
		</div>
	);
};

const AlarmTable: React.FC<AlarmTableProps> = ({
	locations,
	twoWayAlarm,
	onPrepareData,
	isOwner,
}) => {
	const [alarms, setAlarms] = useState<AlarmsState>({});

	// Handler to update alarm values in state
	const handleAlarmChange = (
		locationId: number,
		testKitId: number,
		alarmId: number,
		key: string,
		value: any
	) => {
		const alarmKey = `${locationId}-${testKitId}-${alarmId}`;

		setAlarms((prevAlarms) => {
			const updatedAlarms = { ...prevAlarms };

			if (value === '') {
				// Set the key to an empty string if value is empty
				updatedAlarms[alarmKey] = {
					...(prevAlarms[alarmKey] || {}),
					[key]: '',
				};
			} else {
				const numericValue = Number(value);
				if (!isNaN(numericValue)) {
					// Set the key to the numeric value if value is numeric
					updatedAlarms[alarmKey] = {
						...(prevAlarms[alarmKey] || {}),
						[key]: numericValue,
					};
				}
			}

			return updatedAlarms;
		});
	};

	const prepareDataForApi = () => {
		return Object.entries(alarms).map(([key, updatedValues]) => {
			const [locationIdStr, testKitIdStr, outPutType] = key.split('-');
			const locationId = Number(locationIdStr);
			const testKitId = Number(testKitIdStr);
			const location = locations.find(
				(loc: any) => loc.id === locationId
			);
			const descriptor = location.testKitDescriptors.find(
				(desc: any) => desc.id === testKitId
			);
			const alarm = descriptor.outPutAlarmsVos.find(
				(alarm: { outPutType: string; testkitName: string }) =>
					alarm?.outPutType === outPutType
			);

			//Check for custom alarm ID before sending - cache is already here in 'locations'
			const existingCustomAlarm = location.customAlarmVOs.find((customAlarm: any) => customAlarm.testKitId === testKitId && customAlarm.outputId === alarm?.outPutType);

			if (updatedValues.hiHiValue === undefined && existingCustomAlarm?.hiHiAlarmValue !== undefined) {
				updatedValues.hiHiValue = existingCustomAlarm.hiHiAlarmValue
			}
			if (updatedValues.hiValue === undefined && existingCustomAlarm?.hiAlarmValue !== undefined) {
				updatedValues.hiValue = existingCustomAlarm.hiAlarmValue
			}
			if (updatedValues.loValue === undefined && existingCustomAlarm?.loAlarmValue !== undefined) {
				updatedValues.loValue = existingCustomAlarm.loAlarmValue
			}
			if (updatedValues.loLoValue === undefined && existingCustomAlarm?.loLoAlarmValue !== undefined) {
				updatedValues.loLoValue = existingCustomAlarm.loLoAlarmValue
			}

			return {
				id: existingCustomAlarm?.id,
				dataSetName: alarm?.outPutType,
				units: alarm?.unit,
				dataSetType: 'atp',
				sampleLocationId: location.id,
				status: 'Active',
				testkitOutputId: alarm?.id,
				outPutAlarmsVo: {
					...updatedValues,
					correctiveMessage: 'Corrective Message',
					preventiveMessage: 'Preventive Message',
					greenAlarm: 'Green Alarm',
					outPutType: alarm?.outPutType,
					unit: alarm?.unit,
				},
				lastModificationDate: Date.now(),
			};
		});
	};

	useEffect(() => {
		const data = prepareDataForApi();
		onPrepareData(data);
	}, [alarms, onPrepareData]);
	return (
		<div>
			<Box
				sx={{
					fontFamily: 'Inter, sans-serif',
					fontWeight: 600,
					fontSize: '16px',
					letterSpacing: '0.15px',
					lineHeight: '24px',
					color: '#666666',
					borderBottom: 1,
					borderColor: CONSTANTS.BORDER_COLOR,
					pb: 1,
				}}
			>
				{/* Header */}
				<div className="grid grid-cols-9 !font-inter !text-[#2C2C2C] !text-[14px]">
					<div className="col-span-2 ">Location</div>
					<div className="col-span-1">Test Kit</div>
					<div className={`col-span-${twoWayAlarm ? '1' : '3'}`}>
						Results
					</div>
					{twoWayAlarm && (
						<div className="col-span-5 flex ml-[50px]">
							<div className="mr-10">Immediate</div>
							<div className="mr-6">Preventative</div>
							<div className="mr-8">Good Control </div>{' '}
							<div className="mr-6">Preventative</div>{' '}
							<div>Immediate</div>
						</div>
					)}
					{!twoWayAlarm && (
						<div className="col-span-3 flex ml-[45px]">
							<div className="mr-6">Good Control </div>{' '}
							<div className="mr-6">Preventative</div>{' '}
							<div>Immediate</div>
						</div>
					)}
				</div>
			</Box>
			<div className="!font-jost !text-[#2C2C2C] !text-[14px]">
				{/* Rows */}
				{locations.map((location: any, locIndex: number) =>
					location.testKitDescriptors.map(
						(descriptor: any, descIndex: number) =>
							descriptor?.outPutAlarmsVos.map(
								(alarm: any, alarmIndex: number) => (
									<AlarmRow
										key={`row-${locIndex}-${descIndex}-${alarmIndex}`}
										location={location}
										descriptor={descriptor}
										alarm={alarm}
										twoWayAlarm={twoWayAlarm}
										handleAlarmChange={handleAlarmChange}
										statusColors={statusColors}
										isOwner={isOwner}
									/>
								)
							)
					)
				)}
			</div>
		</div>
	);
};
export default AlarmTable;
